import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { YesNoCell, DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import AddTagDialog from '@pages-clean/risk-assessment/monitoring-toolkit/dialogs/add-tag-dialog';
import DeleteTagDialog from '@pages-clean/risk-assessment/monitoring-toolkit/dialogs/delete-tag-dialog';
import EditTagDialog from '@pages-clean/risk-assessment/monitoring-toolkit/dialogs/edit-tag-dialog';
import { useBoolean } from '@hooks/index';
import { getRiskAssessmentTags } from '@queries/generated/risk-assessment.generated';
import { RISK_ASSESSMENT_TAGS } from '@queries/risk-assessment';

interface TagGridProps {
    canEdit: boolean;
    studyRef: string;
}

interface Tag {
    label: string;
    active: boolean;
}

type TagDto = {
    id?: number;
    label?: string | null;
    active?: boolean | null;
    created?: string;
    createdByName?: string | null;
    updatedDate?: string;
    updatedByName?: string | null;
    recordId?: number;
    uses?: number;
};

const RiskAssessmentTagGrid = ({ canEdit, studyRef }: TagGridProps) => {
    const [selectedItem, setSelectedItem] = useState<TagDto | undefined>(undefined);
    const [tagToEdit, setTagToEdit] = useState<TagDto | undefined>(undefined);
    const [tags, setTags] = useState<unknown[]>([]);
    const [existingTags, setExistingTags] = useState<Tag[]>([]);
    const selectLastItemRef = useRef<boolean>(false);
    const { TooltipCell } = createGridCustomCells();
    const { t: tagGridTranslation }: { t: (key: string) => string } = useTranslation('ram', {
        keyPrefix: 'monitoringToolkit',
    });

    const { loading, error, data, refetch } = useQuery<getRiskAssessmentTags>(RISK_ASSESSMENT_TAGS, {
        variables: { studyId: studyRef || null },
    });

    useEffect(() => {
        if (!loading && data && data.riskAssessmentTags) {
            const loadedData = data.riskAssessmentTags.map((tag) => {
                return {
                    ...tag,
                    createdByName: `${tag?.createdBy?.firstName} ${tag?.createdBy?.lastName}`,
                    uses: 0,
                    label: tag?.name,
                    updatedByName: `${tag?.updatedBy?.firstName} ${tag?.updatedBy?.lastName}`,
                };
            });
            const sortedData = orderBy(loadedData, [{ field: 'created', dir: 'asc' }]);

            const existing: Tag[] = loadedData?.map((d) => {
                return {
                    label: d?.name!,
                    active: d?.active!,
                };
            });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setTags(sortedData);
            if (selectLastItemRef.current) {
                selectLastItemRef.current = false;
                setSelectedItem(sortedData[sortedData.length - 1]);
            }
            setExistingTags(existing);
            /* eslint-enable @eslint-react/set-state-in-effect */
        }
    }, [loading, data]);

    const showAddDialog = useBoolean(false);
    const showEditDialog = useBoolean(false);
    const showDeleteDialog = useBoolean(false);

    const showAdd = () => {
        showAddDialog.setTrue();
        showEditDialog.setFalse();
        showDeleteDialog.setFalse();
    };

    const showEdit = () => {
        showAddDialog.setFalse();
        showEditDialog.setTrue();
        showDeleteDialog.setFalse();
    };

    const showDelete = () => {
        showAddDialog.setFalse();
        showEditDialog.setFalse();
        showDeleteDialog.setTrue();
    };

    const closeAllDialogs = () => {
        showAddDialog.setFalse();
        showEditDialog.setFalse();
        showDeleteDialog.setFalse();
    };

    const onSubmitHandler = async (selectLastItem: boolean) => {
        selectLastItemRef.current = selectLastItem;
        closeAllDialogs();
        await refetch();
    };

    const onSubmitEditHandler = async () => {
        closeAllDialogs();
        await refetch();
    };

    const onDeleteSubmitHandler = async () => {
        setSelectedItem(undefined);
        await onSubmitHandler(false);
    };

    const deleteTagRow = (dataItem: unknown) => {
        const tagDto = dataItem as TagDto;
        setTagToEdit(tagDto);
        showDelete();
    };

    const editTagRow = (dataItem: unknown) => {
        const tagDto = dataItem as TagDto;
        setSelectedItem(undefined);
        setTagToEdit(tagDto);
        showEdit();
    };

    const columns = useMemo(() => {
        const tooltipCellTwoLine = TooltipCell({
            isCsv: false,
            maxLines: 2,
        });

        const cols: GridColumnProps[] = [
            {
                id: 'recordId',
                field: 'recordId',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.recordId'),
                width: 1,
                minWidth: 140,
                maxLength: 30,
                cells: { data: tooltipCellTwoLine },
                filter: 'numeric',
            },
            {
                id: 'uses',
                field: 'uses',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.uses'),
                width: 1,
                minWidth: 140,
                maxLength: 30,
                cells: { data: tooltipCellTwoLine },
                filter: 'numeric',
            },
            {
                id: 'label',
                field: 'label',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.label'),
                width: 1,
                minWidth: 140,
                maxLength: 30,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'active',
                field: 'active',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.active'),
                width: 1,
                minWidth: 140,
                cells: { data: YesNoCell },
                filter: 'boolean',
            },
            {
                id: 'createdByName',
                field: 'createdByName',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.createdBy'),
                width: 1,
                minWidth: 140,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'created',
                field: 'created',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.createdOn'),
                width: 1,
                minWidth: 140,
                cells: { data: DateCell },
                filter: 'date',
            },

            {
                id: 'updatedByName',
                field: 'updatedByName',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.updatedBy'),
                width: 1,
                minWidth: 140,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'updated',
                field: 'updated',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.updatedOn'),
                width: 1,
                minWidth: 140,
                cells: { data: DateCell },
                filter: 'date',
            },
        ];

        return cols;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    if (!data) {
        return (
            <Card className="placeholder-card" data-testid="grid" title={tagGridTranslation('manageTagsGrid.title')}>
                {loading ? (
                    <Loading />
                ) : (
                    <InfoField message={tagGridTranslation('emptyMessage')} type="Info" layout="center" />
                )}
            </Card>
        );
    }

    if (error) return <ErrorMessage />;

    let menuActions: RowMenuAction[] | undefined;
    if (canEdit) {
        menuActions = [
            {
                id: 1,
                label: tagGridTranslation('manageTagsGrid.actions.editLabel'),
                onClick: editTagRow,
                icon: ['far', 'pen-to-square'],
            },
            {
                id: 2,
                label: tagGridTranslation('manageTagsGrid.actions.deleteLabel'),
                onClick: deleteTagRow,
                icon: ['fas', 'trash-xmark'],
                colorProp: 'Red',
            },
        ];
    }

    return (
        <>
            {showAddDialog.value && (
                <AddTagDialog
                    studyRef={studyRef}
                    onClose={closeAllDialogs}
                    onSubmit={() => onSubmitHandler(true)}
                    existingTags={existingTags}
                />
            )}
            {showEditDialog.value && (
                <EditTagDialog
                    tagId={tagToEdit?.id}
                    studyRef={studyRef}
                    onClose={closeAllDialogs}
                    onSubmit={() => onSubmitEditHandler()}
                    existingTags={existingTags}
                    tagLabel={tagToEdit?.label}
                    isActive={tagToEdit?.active ?? undefined}
                    activeInstances={tagToEdit?.uses}
                />
            )}
            {showDeleteDialog.value && (
                <DeleteTagDialog
                    studyRef={studyRef}
                    tagId={tagToEdit?.id}
                    activeInstances={tagToEdit?.uses}
                    onClose={closeAllDialogs}
                    onSubmit={() => onDeleteSubmitHandler()}
                />
            )}
            <Grid
                dataItemKey="id"
                title={tagGridTranslation('manageTagsGrid.title')}
                maxHeight="auto"
                testDataId="all-tags-grid"
                data={tags}
                columns={columns}
                loading={loading}
                useLoadingOverlay
                rowReorder={false}
                gridDownloadDisabled
                gridSettingsMenuDisabled
                addCallback={showAdd}
                canEdit={canEdit}
                selectable={{ enabled: true, mode: 'single' }}
                defaultSelectedItem={selectedItem}
                rowMenuActions={menuActions}
                showAddIcon={canEdit}
            />
        </>
    );
};

export default RiskAssessmentTagGrid;
